import { useMemo, useState } from "react";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { differenceWith } from "lodash";
import { GripVertical, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  useComboboxAnchor,
} from "@/components/ui/combobox";
import { Label } from "@/components/ui/label";
import { REQUIRED_MARK } from "@/components/BaseForm/fields/fieldClasses";

const SortableStatusRow = ({ name, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: name });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="tw:flex tw:items-center tw:gap-2 tw:rounded-md tw:border tw:border-(--color-border) tw:bg-(--color-surface-overlay) tw:px-2 tw:py-1.5 tw:text-(--color-text-primary)"
    >
      <button
        type="button"
        aria-label="Drag to reorder"
        className="tw:cursor-grab tw:border-none tw:bg-transparent tw:p-1 tw:text-(--color-text-muted) tw:hover:text-foreground tw:active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="tw:size-4" />
      </button>
      <span className="tw:flex-1">{name}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="Remove"
        onClick={() => onDelete(name)}
      >
        <Trash2 />
      </Button>
    </div>
  );
};

const RevenueOpportunityStatusSelector = ({
  revenueOpportunityStatuses,
  setRevenueOpportunityStatuses,
  allRevenueOpportunityStatuses,
}) => {
  const [selection, setSelection] = useState(null);
  const anchorRef = useComboboxAnchor();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    // Without the sortable coordinate getter, arrow keys translate the node by
    // a flat 25px — less than a row, so the drag never crosses into the next
    // index and keyboard reordering silently does nothing.
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const options = useMemo(
    () =>
      differenceWith(
        allRevenueOpportunityStatuses,
        revenueOpportunityStatuses,
        (a, b) => a.id === b.id
      ).map(({ id, name }) => ({ value: name, label: name, id })),
    [allRevenueOpportunityStatuses, revenueOpportunityStatuses]
  );

  const handleAdd = () => {
    if (!selection) {
      return;
    }
    const exists = revenueOpportunityStatuses.some(
      item => item.name === selection.value
    );
    if (!exists) {
      setRevenueOpportunityStatuses([
        ...revenueOpportunityStatuses,
        { id: selection.id, name: selection.value },
      ]);
    }
    setSelection(null);
  };

  const handleDelete = name => {
    setRevenueOpportunityStatuses(
      revenueOpportunityStatuses.filter(item => item.name !== name)
    );
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = revenueOpportunityStatuses.findIndex(
      item => item.name === active.id
    );
    const newIndex = revenueOpportunityStatuses.findIndex(
      item => item.name === over.id
    );
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }
    setRevenueOpportunityStatuses(
      arrayMove(revenueOpportunityStatuses, oldIndex, newIndex)
    );
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      <div className="tw:flex tw:flex-col tw:gap-1">
        <Label className={REQUIRED_MARK}>Revenue Opportunity Statuses</Label>
        <div className="tw:flex tw:items-end tw:gap-2">
          <Combobox
            items={options}
            value={selection}
            onValueChange={setSelection}
            itemToStringLabel={item => item?.label ?? ""}
          >
            <div ref={anchorRef} className="tw:flex-1">
              <ComboboxInput
                placeholder="Select…"
                showTrigger={false}
                className="tw:w-full"
              />
            </div>
            <ComboboxContent anchor={anchorRef}>
              <ComboboxEmpty>No matches</ComboboxEmpty>
              <ComboboxList>
                {item => (
                  <ComboboxItem key={item.value} value={item}>
                    {item.label}
                  </ComboboxItem>
                )}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
          <Button
            type="button"
            variant="outline"
            onClick={handleAdd}
            disabled={!selection}
          >
            <Plus data-icon="inline-start" />
            Add
          </Button>
        </div>
      </div>
      {revenueOpportunityStatuses.length === 0 ? (
        <p className="tw:py-2 tw:text-center tw:text-sm tw:text-(--color-text-muted) tw:italic">
          No Revenue Opportunity Statuses included in this preset
        </p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={revenueOpportunityStatuses.map(({ name }) => name)}
            strategy={verticalListSortingStrategy}
          >
            <div className="tw:flex tw:flex-col tw:gap-1">
              {revenueOpportunityStatuses.map(({ name }) => (
                <SortableStatusRow
                  key={name}
                  name={name}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};

export default RevenueOpportunityStatusSelector;
